import { combineLatest, Subject, animationFrameScheduler, merge, fromEvent, } from 'rxjs';
import { pairwise, takeUntil, throttleTime, map } from 'rxjs/operators';
import { ToolInteractionEngine } from '../engines/interaction/tool_interaction_engine';
export class InteractionManager {
    constructor(context) {
        this.context = context;
        /**
         *
         * When the video canvas styles are set to {width: 100%, height: 100%, objectFit: contain},
         * we need to determine the width and height of the VISIBLE video within the canvas when caculating where to show tool interactions.
         *
         * 'determineRenderedCanvasDims' calculates the width and height of the visible canvas within defined container dimensions
         *
         * @param canvasWidth static width of the canvas element (canvasElement.width)
         * @param canvasHeight static height of the canvas element (canvasElement.height)
         * @param clientWidth width of the canvas as it is rendered (canvasElement.clientWidth)
         * @param clientHeight height of the canvas as it is rendered (canvasElement.clientHeight)
         * @returns the width and height of the visible canvas image -- does not include padding or borders (letterboxing)
         */
        this.determineRenderedCanvasDims = (canvasWidth, canvasHeight, clientWidth, clientHeight) => {
            const isLandscape = canvasWidth > canvasHeight;
            let renderedHeight;
            let renderedWidth;
            const aspectRatioConversion = isLandscape
                ? canvasWidth / canvasHeight
                : canvasHeight / canvasWidth;
            if (isLandscape) {
                renderedWidth = clientWidth;
                renderedHeight = clientWidth / aspectRatioConversion;
                // canvas is constrained if it overflows parent width
                if (renderedWidth > clientWidth) {
                    renderedWidth = clientWidth;
                    renderedHeight = clientWidth * aspectRatioConversion;
                }
                // canvas is constrained if it overflows parent height
                if (renderedHeight > clientHeight) {
                    renderedHeight = clientHeight;
                    renderedWidth = clientHeight * aspectRatioConversion;
                }
            }
            else {
                renderedWidth = clientHeight / aspectRatioConversion;
                renderedHeight = clientHeight;
                // canvas is constrained if it overflows parent width
                if (renderedWidth > clientWidth) {
                    renderedWidth = clientWidth;
                    renderedHeight = clientWidth * aspectRatioConversion;
                }
                // canvas is constrained if it overflows parent height
                if (renderedHeight > clientHeight) {
                    renderedHeight = clientHeight;
                    renderedWidth = clientHeight * aspectRatioConversion;
                }
            }
            return {
                renderedWidth,
                renderedHeight,
            };
        };
        const toolEngine = new ToolInteractionEngine(context.roomSession);
        this.toolMap = {
            LASER: toolEngine,
            MARKER: toolEngine,
            TEXT: undefined,
        };
    }
    startListening(cameraFrame) {
        const unsubscriber = new Subject();
        const mouseEvents = merge(fromEvent(this.context.container, 'mousemove'), fromEvent(this.context.container, 'mouseleave'), fromEvent(this.context.container, 'mouseup'), fromEvent(this.context.container, 'mousedown')).pipe(takeUntil(unsubscriber), map((event) => {
            event.preventDefault();
            let xAdjustment = 0;
            let yAdjustment = 0;
            if (event.target instanceof HTMLCanvasElement) {
                const { renderedHeight, renderedWidth } = this.determineRenderedCanvasDims(event.target.width, event.target.height, event.target.clientWidth, event.target.clientHeight);
                // X or Y axis adjustments are necessary to account for canvas letterboxing to maintain aspect ratio
                // With canvas styles set to {width: 100%, height: 100%, objectFit: contain}, we need to determine the width and height of the VISIBLE video within the canvas when calculating the mouse interaction positions that will translate to visible tool UI.
                // no Y axis adjustment needed, adjust for X axis
                if (event.target.clientHeight === renderedHeight) {
                    xAdjustment = event.target.clientWidth - renderedWidth;
                }
                // no X axis adjustment needed, adjust for Y axis
                if (event.target.clientWidth === renderedWidth) {
                    yAdjustment = event.target.clientHeight - renderedHeight;
                }
            }
            return {
                xPercent: event.target instanceof HTMLCanvasElement
                    ? (event.offsetX - xAdjustment / 2) /
                        (event.target.clientWidth - xAdjustment)
                    : -1,
                yPercent: event.target instanceof HTMLCanvasElement
                    ? (event.offsetY - yAdjustment / 2) /
                        (event.target.clientHeight - yAdjustment)
                    : -1,
                inFrame: event.target instanceof HTMLCanvasElement,
                // Buttons Bitmask https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
                buttons: {
                    primary: (event.buttons & 1) > 0,
                    secondary: (event.buttons & 2) > 0,
                    auxilary: (event.buttons & 4) > 0,
                    fourth: (event.buttons & 8) > 0,
                    fifth: (event.buttons & 16) > 0,
                },
            };
        }));
        // Fire combined signals from mouse events, active tool, and camera frame.
        combineLatest([mouseEvents, this.context.roomSession.tools.activeTool, cameraFrame])
            .pipe(takeUntil(unsubscriber), throttleTime(0, animationFrameScheduler))
            .subscribe(([coord, tool]) => {
            this.toolMap[tool]?.handleMouseInteractionEvent(coord, tool);
        });
        this.context.roomSession.tools.activeTool
            .pipe(pairwise(), takeUntil(unsubscriber))
            .subscribe(([prevTool, nextTool]) => {
            if (prevTool !== nextTool) {
                const prevEngine = this.toolMap[prevTool];
                if (prevEngine?.deactivate) {
                    prevEngine.deactivate(prevTool);
                }
                const nextEngine = this.toolMap[nextTool];
                if (nextEngine?.activate) {
                    nextEngine.activate(nextTool);
                }
            }
        });
        return () => {
            unsubscriber.next(undefined);
        };
    }
}
//# sourceMappingURL=interaction_manager.js.map